package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIfNotNull
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.random.Random

/**
 * Whether the value is a figure or a sentence — declared by the caller, never sniffed.
 *
 * "6" and "6 owner and admin seats" are both legitimate values of the same row, and a rule that
 * guesses between them is a layout decision the caller can neither see nor correct. Worse, it
 * changes with the data: a workspace that reads `$114.00` today reads `No invoices yet` tomorrow,
 * and the row would silently re-align underneath a reader who is comparing two screenshots.
 */
enum class LKeyValueKind {
    /**
     * Right-aligned and tabular. Every figure on the surface then stacks on one edge, which is the
     * whole reason a receipt can be read without arithmetic.
     */
    NUMERIC,

    /**
     * Left-aligned, starting at the label column's right edge. A sentence set ragged-*left* in a
     * right-aligned slot has a new start position on every line and is genuinely hard to read.
     */
    PROSE,
}

/** The trailing link's words and where they go. Both or neither — never a href without a label. */
data class LKeyValueLink(
    val label: String,
    val href: String,
)

val LKeyValueRowStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .gap(Space.LG)
    }
    // Separation is a hairline, and `border` is the divider token — the row is not interactive, so
    // this is the one place the 1.32:1 token is the correct one. The last row of a group opts out
    // so the card's own edge closes the list instead of a second line doing it 1px above.
    cssRule("[data-hairline=\"true\"]") {
        Modifier.borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
    }
}

val LKeyValueRowLabelStyle = CssStyle.base {
    Modifier
        // Fixed, not intrinsic: a stack of these rows only reads as a column when every value
        // starts at the same x, and an auto-width label makes that depend on the longest word.
        .width(200.px)
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
        .styleModifier { property("flex-shrink", "0") }
}

val LKeyValueRowValueStyle = CssStyle.base {
    Modifier
        .flexGrow(1)
        // Without this a long value refuses to shrink below its content width and pushes the
        // trailing link off the row rather than wrapping.
        .minWidth(0.px)
        .color(colorMode.toSitePalette().textPrimary)
        .fontSize(TypeScale.Body)
        .lineHeight(TypeScale.BodyLineHeight)
        // The receipt spec puts the note line 4 below the value's line box, not hard against it.
        // Harmless when there is no caption — a one-child flex column has no gap to apply.
        .gap(Space.XS)
        // On the column, so the caption's figures line up with the value's.
        .tabularNumbers()
}

val LKeyValueRowCaptionStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
}

val LKeyValueRowLinkStyle = CssStyle {
    base {
        Modifier
            // 40 in both dimensions by min-size and centring, not by vertical padding: an 18px
            // line box plus `padding: 11 0` would be the only way to reach 40 that way, and 11 is
            // not on the space scale.
            .minWidth(HIT_AREA_FLOOR_PX.px)
            .minHeight(HIT_AREA_FLOOR_PX.px)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .color(colorMode.toSitePalette().accent)
            .fontSize(TypeScale.Small)
            .fontWeight(500)
            .whiteSpace(WhiteSpace.NoWrap)
            .borderRadius(Radius.Control)
            .textDecorationLine(TextDecorationLine.None)
            .transitionColors()
            .styleModifier {
                property("display", "inline-flex")
                property("flex-shrink", "0")
            }
    }
    // Underline on both, because hue alone is not an affordance and a focus ring alone disappears
    // for anyone watching the text rather than the outline.
    hover { Modifier.textDecorationLine(TextDecorationLine.Underline) }
    focusVisible {
        Modifier
            .textDecorationLine(TextDecorationLine.Underline)
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

/**
 * A labelled fact: a label in a fixed column, a value, and optionally a caption and a trailing link.
 *
 * The most repeated row on the money surfaces — billing's "Last invoice" and "Billable seats", and
 * every line of the run receipt's left card.
 *
 * @param kind whether [value] is a figure or a sentence. Governs alignment — see [LKeyValueKind].
 * @param caption a second line beneath the value, in `textMuted`: the receipt's note lines.
 * @param compact the receipt's 48-tall row rather than billing's 56.
 * @param hairline draws the row's bottom rule. **The caller clears it on the last row of a group**,
 *   so the card's own edge closes the list instead of a second line doing it 1px above. Unlike
 *   [LStatRow], which is handed the whole list and owns its own dividers, this component is called
 *   once per row and cannot see which row is last — the bookkeeping is the caller's, and it is the
 *   first thing to go wrong the day a row becomes conditional.
 */
@Composable
fun LKeyValueRow(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    kind: LKeyValueKind = LKeyValueKind.NUMERIC,
    caption: String? = null,
    link: LKeyValueLink? = null,
    compact: Boolean = false,
    hairline: Boolean = true,
) {
    LKeyValueRow(
        label = label,
        modifier = modifier,
        kind = kind,
        caption = caption,
        link = link,
        compact = compact,
        hairline = hairline,
    ) {
        Span { Text(value) }
    }
}

/**
 * As [LKeyValueRow], but the value is composed rather than a string.
 *
 * The receipt's card puts a status badge, a monospaced slug and a row of language chips in this
 * slot, and billing puts an [LSkeleton] there while the invoice is still loading — the label and
 * the hairline are already correct by then, so nothing moves when it resolves.
 *
 * Every parameter carries the meaning it has on the string overload above, [hairline] included.
 */
@Composable
fun LKeyValueRow(
    label: String,
    modifier: Modifier = Modifier,
    kind: LKeyValueKind = LKeyValueKind.NUMERIC,
    caption: String? = null,
    link: LKeyValueLink? = null,
    compact: Boolean = false,
    hairline: Boolean = true,
    value: @Composable () -> Unit,
) {
    val labelId = remember { "lingvo-kv-${Random.nextInt(1_000_000)}" }
    // A caption is a second line and is paid for as one, rather than being squeezed into the
    // single-line box. min-height rather than height so a value that does wrap grows the row
    // instead of spilling out of it.
    val minRowHeight = (if (compact) 48 else 56) + (if (caption != null) 20 else 0)
    Row(
        LKeyValueRowStyle.toModifier()
            .minHeight(minRowHeight.px)
            .attr("data-hairline", hairline.toString())
            // "All invoices" does not say which fact it belongs to. Naming the pair as a group is
            // what supplies that when the link is reached by keyboard, and it costs the rows
            // without a link nothing because they do not get it.
            .thenIfNotNull(link) {
                Modifier.attr("role", "group").attr("aria-labelledby", labelId)
            }
            .then(modifier),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Span(
            LKeyValueRowLabelStyle.toModifier().toAttrs {
                if (link != null) id(labelId)
            },
        ) {
            Text(label)
        }
        Column(
            LKeyValueRowValueStyle.toModifier().textAlign(kind.textAlign),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = kind.horizontalAlignment,
        ) {
            value()
            if (caption != null) {
                Div(LKeyValueRowCaptionStyle.toModifier().toAttrs()) {
                    Text(caption)
                }
            }
        }
        if (link != null) {
            Link(
                path = link.href,
                text = link.label,
                modifier = LKeyValueRowLinkStyle.toModifier(),
                // What actually wins is the layer, not the variants: a bare `CssStyle { }` lands in
                // `general-styles`, which sits above `component-styles` and `component-variants`, so
                // this file's colour and hover rule beat Silk's `:link` / `:visited` / `:hover` even
                // at lower specificity. The variants are carried anyway because every other link in
                // `ds/` carries them, and a link here that opted out would be the odd one to explain.
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
            )
        }
    }
}

/** Governs where a wrapped value's later lines start. */
private val LKeyValueKind.textAlign: TextAlign
    get() = when (this) {
        LKeyValueKind.NUMERIC -> TextAlign.End
        LKeyValueKind.PROSE -> TextAlign.Start
    }

/** Governs which edge the value and its caption sit against. */
private val LKeyValueKind.horizontalAlignment: Alignment.Horizontal
    get() = when (this) {
        LKeyValueKind.NUMERIC -> Alignment.End
        LKeyValueKind.PROSE -> Alignment.Start
    }
